import { Check, ArrowUpRight, Shirt, Timer, Baby, Medal } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader, CTAButton, Kicker } from "../components/Bits";
import { Reveal } from "../components/Reveal";
import { EVENT, LINKS } from "../lib/site";

const SHIRT_FRONT = `${process.env.PUBLIC_URL}/media/shirt-front.png`;
const SHIRT_BACK = `${process.env.PUBLIC_URL}/media/shirt-back.png`;

const TIERS = [
  { name: "Kids 1 km Dash", price: "$20", icon: Baby, perks: ["Ages 4–12", "Free event t-shirt", "Post-race snacks"] },
  { name: "5 km Run / Walk", price: "$65", icon: Timer, perks: ["Chip timing", "Free event t-shirt", "Age group medals", "Post-race snacks"], featured: true },
];

export default function Registration() {
  return (
    <>
      <Seo title="Registration" path="/registration" description="Register for the Grimsby On The Lake Turkey Trot 5 km Run/Walk or Kids 1 km Dash." />
      <PageHeader
        chapter="join the flock"
        title="Register for the trot."
        sub={`Secure your spot for ${EVENT.dateLabel}. Registration is quick, and every entry directly supports ${EVENT.charity}.`}
      />

      <section className="mx-auto max-w-3xl px-5 sm:px-8 pb-16 grid gap-6 sm:grid-cols-2">
        {TIERS.map((t, i) => {
          const Icon = t.icon;
          return (
            <Reveal key={t.name} delay={i * 0.08}>
              <div className={`h-full rounded-3xl p-8 sm:p-10 border transition-all duration-300 hover:-translate-y-1 ${t.featured ? "bg-terracotta text-white border-terracotta shadow-warm-lg" : "bg-white text-ink border-black/5 shadow-warm"}`}>
                <Icon size={28} className={t.featured ? "text-ochre" : "text-terracotta"} />
                <h3 className="mt-5 font-display text-2xl font-semibold">{t.name}</h3>
                <p className="mt-2 font-display text-5xl font-semibold">{t.price}</p>
                <ul className="mt-6 space-y-2 text-sm">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-center gap-2">
                      <Check size={16} className={t.featured ? "text-ochre" : "text-forest"} /> {p}
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <CTAButton href={LINKS.register} variant={t.featured ? "forest" : "primary"}>
                    Register <ArrowUpRight size={16} />
                  </CTAButton>
                </div>
              </div>
            </Reveal>
          );
        })}
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <Kicker>every entry includes</Kicker>
            <h2 className="mt-2 font-display font-semibold tracking-tight text-4xl sm:text-5xl">
              A free tee & a shot at the podium.
            </h2>
            <p className="mt-4 text-ink-soft leading-relaxed">
              Every registered trotter takes home our exclusive event t-shirt, and 5 km age group winners earn medals at the awards ceremony.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          <Reveal className="lg:col-span-2">
            <div className="h-full rounded-3xl bg-black border border-white/10 shadow-warm-lg p-8 sm:p-10">
              <div className="flex items-center gap-2 text-ochre font-semibold"><Shirt size={20} /> Free event t-shirt</div>
              <div className="mt-6 grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-[radial-gradient(closest-side,rgba(245,222,179,0.14),transparent)]" />
                    <img src={SHIRT_FRONT} alt="Turkey Trot t-shirt front" className="relative w-full h-72 object-contain drop-shadow-[0_20px_28px_rgba(0,0,0,0.55)]" data-testid="shirt-front" />
                  </div>
                  <p className="mt-2 text-xs uppercase tracking-widest text-cream/60">Front</p>
                </div>
                <div className="text-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-[radial-gradient(closest-side,rgba(245,222,179,0.14),transparent)]" />
                    <img src={SHIRT_BACK} alt="Turkey Trot t-shirt back" className="relative w-full h-72 object-contain drop-shadow-[0_20px_28px_rgba(0,0,0,0.55)]" data-testid="shirt-back" />
                  </div>
                  <p className="mt-2 text-xs uppercase tracking-widest text-cream/60">Back</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl bg-forest text-cream shadow-warm-lg p-8 sm:p-10 flex flex-col justify-center">
              <div className="grid place-items-center h-16 w-16 rounded-2xl bg-ochre/20 text-ochre">
                <Medal size={30} />
              </div>
              <h3 className="mt-6 font-display text-3xl font-semibold">Age group medals</h3>
              <p className="mt-3 text-cream/75 leading-relaxed">
                In our inaugural year, medals go to the top finishers in each 5 km age group. Race hard, and join us at the 10:30 AM awards ceremony.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 sm:px-8 pb-24 sm:pb-32 text-center">
        <Reveal>
          <Kicker>secure checkout</Kicker>
          <p className="mt-3 text-ink-soft leading-relaxed">
            Registration is handled by our official race partner, Race Roster. Click any button above to
            complete your entry, choose your shirt size and pay securely.
          </p>
        </Reveal>
      </section>
    </>
  );
}
